import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    // Record which product option the shopper changed before variant-picker dispatches its update.
    target?.addEventListener('change', this.#handleVariantInputChange, { capture: true, signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // CFI: choosing a photo also chooses its colour.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    for (const type of ['pointerdown', 'touchstart', 'touchend', 'wheel', 'keydown']) {
      this.addEventListener(type, this.#markUserInput, { passive: true, signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#colourSyncTimer);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */

  // Used only for the next product-selection update. Size/other selections do not scroll.
  #pendingColorSelection = false;

  #handleVariantInputChange = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.closest('variant-picker')) return;

    // A colour chosen from a photo: the shopper is already looking at the gallery.
    if (this.#syncingColourFromMedia) {
      this.#pendingColorSelection = false;
      return;
    }

    const option = event.target instanceof HTMLSelectElement
      ? event.target.selectedOptions[0]
      : event.target;
    const optionName = option?.dataset?.optionName?.replace(/\s+/g, '') ?? '';
    this.#pendingColorSelection = /^colou?r\d*$/i.test(optionName);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // A colour chosen from a photo keeps the gallery as it is, on that photo.
    if (this.#syncingColourFromMedia) {
      this.#syncPromise = event.promise;
      event.promise?.catch(() => {});
      return;
    }

    const scrollToColorOnMobile =
      this.#pendingColorSelection &&
      window.matchMedia('(max-width: 749px)').matches &&
      !this.closest('dialog') &&
      !event.target.closest('quick-add-dialog');
    this.#pendingColorSelection = false;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        if (scrollToColorOnMobile) {
          requestAnimationFrame(() => {
            if (!newMediaGallery.isConnected) return;

            // Show the newly selected colour's image without obscuring it under a sticky header.
            const header = document.querySelector('header');
            const stickyHeaderHeight =
              header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)
                ? header.getBoundingClientRect().height
                : 0;
            const scrollTop =
              window.scrollY + newMediaGallery.getBoundingClientRect().top - stickyHeaderHeight - 12;

            window.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /* CFI: choosing a photo also chooses its colour. Data comes from blocks/_product-media-gallery.liquid. */

  #syncingColourFromMedia = false;
  #syncRun = 0;
  /** @type {Promise<any> | undefined} */
  #syncPromise;
  #lastUserInput = 0;
  /** @type {number | undefined} */
  #colourSyncTimer;
  /** @type {any} */
  #colourData;

  #markUserInput = () => {
    this.#lastUserInput = performance.now();
  };

  /**
   * @param {Event} event - The slideshow:select event.
   */
  #handleSlideSelect = (event) => {
    if (event.target !== this.refs.slideshow) return;
    // The slideshow also reports scrolls it makes itself; follow only the shopper's own browsing.
    if (performance.now() - this.#lastUserInput > 2000) return;

    const slide = /** @type {SlideshowSelectEvent} */ (event).detail?.slide;
    const mediaId = slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    clearTimeout(this.#colourSyncTimer);
    if (!mediaId) return;

    // Wait for the shopper to settle on a photo before changing the selection.
    this.#colourSyncTimer = setTimeout(() => this.#selectColourForMedia(mediaId), 300);
  };

  /**
   * Selects the colour (and, if that colour needs it, the other options) shown in a photo.
   * @param {string} mediaId - The photo's media id.
   */
  async #selectColourForMedia(mediaId) {
    const run = ++this.#syncRun;
    const data = this.#getColourData();
    if (!this.isConnected || !data) return;

    const picker = [...(this.closest('.shopify-section, dialog')?.querySelectorAll('variant-picker') ?? [])].find(
      (element) =>
        element instanceof HTMLElement &&
        element.dataset.productId === String(data.product) &&
        !element.closest('product-card')
    );
    if (!picker) return;

    const index = data.colour;
    /** @type {[number, string[], boolean][]} */
    const variants = data.variants;
    const byPhoto = variants.filter(([media]) => String(media) === mediaId);

    let colour;
    if (byPhoto.length) {
      const colours = new Set(byPhoto.map(([, options]) => options[index]));
      if (colours.size !== 1) return;
      [colour] = colours;
    } else {
      colour = this.#colourNamedIn(data.alts[mediaId], data);
    }
    if (!colour) return;

    const radios = /** @type {HTMLInputElement[]} */ ([
      ...picker.querySelectorAll('input[type="radio"][data-option-name]'),
    ]);
    /** @type {string[]} */
    const optionNames = data.options;
    const current = optionNames.map((name) => radios.find((radio) => radio.dataset.optionName === name && radio.checked)?.value);
    if (current[index] === colour) return;

    // Keep the shopper's other choices (size, insert, length) when the new colour comes in them.
    const keepsOthers = (/** @type {string[]} */ options) =>
      options.every((value, i) => i === index || value === current[i]);
    const candidates = byPhoto.length ? byPhoto : variants.filter(([, options]) => options[index] === colour);
    const target =
      candidates.find(([, options]) => keepsOthers(options)) ??
      variants.find(([, options]) => options[index] === colour && keepsOthers(options)) ??
      candidates.find(([, , available]) => available) ??
      candidates[0];
    if (!target) return;

    const changes = optionNames
      .map((name, i) => ({ name, value: target[1][i], isColour: i === index }))
      .filter(({ value }, i) => value !== current[i])
      .sort((a, b) => Number(a.isColour) - Number(b.isColour));

    for (const [step, { name, value }] of changes.entries()) {
      const radio = /** @type {HTMLInputElement | undefined} */ (
        [...picker.querySelectorAll('input[type="radio"][data-option-name]')].find(
          (input) => input instanceof HTMLInputElement && input.dataset.optionName === name && input.value === value
        )
      );
      if (!radio) return;

      this.#syncPromise = undefined;
      this.#syncingColourFromMedia = true;
      try {
        radio.click();
      } finally {
        this.#syncingColourFromMedia = false;
      }

      // The next option waits for the picker to reflect this one, or its variant id would be stale.
      if (step < changes.length - 1) {
        await this.#syncPromise?.catch(() => {});
        if (run !== this.#syncRun || !this.isConnected) return;
      }
    }
  }

  /**
   * The one colour an alt text names, if exactly one. "Natural White" wins over "White".
   * @param {string | undefined} alt
   * @param {any} data
   * @returns {string | null}
   */
  #colourNamedIn(alt, data) {
    if (!alt || alt.trim().toLowerCase() === String(data.title).trim().toLowerCase()) return null;

    const words = (/** @type {string} */ text) => ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
    const altWords = words(alt);
    /** @type {string[]} */
    const colours = [...new Set(data.variants.map((/** @type {[number, string[], boolean]} */ [, options]) => options[data.colour]))];
    const named = colours.filter((colour) => altWords.includes(words(colour)));
    const longest = named.filter((colour) => !named.some((other) => other !== colour && words(other).includes(words(colour))));

    return longest.length === 1 ? longest[0] ?? null : null;
  }

  #getColourData() {
    if (this.#colourData === undefined) {
      const script = this.parentElement?.querySelector(':scope > script[data-cfi-media-colours]');
      try {
        this.#colourData = script ? JSON.parse(script.textContent ?? '') : null;
      } catch {
        this.#colourData = null;
      }
    }
    return this.#colourData;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
